/**
 * Pure helpers for the asker's match-arrival banner (async-matching, issue #22).
 *
 * The banner is raised from the app-wide SSE handler when new helpers arrive for a
 * question the current user asked and they are NOT already looking at it. These two
 * pure functions carry the branchy bits - the names-first title (with a warm fallback)
 * and the "am I already on this question's screen?" route check - so they can be unit
 * tested without the SSE harness. See `useRealtimeInvalidation`'s
 * `question.matches_updated` case for the wiring.
 */

/** The banner's key in `@utils/arrivalBanners`: one per question, so a newer arrival replaces it. */
export const matchArrivalBannerKey = (questionId: string): string =>
  `question-matches:${questionId}`;

/**
 * Build the banner title, names-first.
 *
 * Given the resolvable helper first names (from the suggested-helpers cache) it names up
 * to three; beyond that it names two and counts the rest. The "+N others" tail is always
 * sized off `newHelperCount` (the number of helpers the event actually announced), not
 * just `names.length` - resolved names are a subset of the announced arrivals (the
 * suggested-helpers cache can still be resolving some of them), so a tail sized from
 * `names.length` alone would undercount whenever fewer names have resolved than arrived.
 * When no names are resolvable at all (the cache is still cold) it falls back to warm,
 * count-aware phrasing driven by `newHelperCount`.
 *
 * Examples (as `buildMatchArrivalTitle(firstNames, newHelperCount)`):
 *   (['Alice'], 1)                       -> "Alice can help"
 *   (['Alice', 'Bob'], 2)                -> "Alice and Bob can help"
 *   (['Alice', 'Bob', 'Carla'], 3)       -> "Alice, Bob and Carla can help"
 *   (['Alice', 'Bob', 'Carla', 'Di'], 4) -> "Alice, Bob and 2 others can help"
 *   (['Alice', 'Bob', 'Carla', 'Di'], 6) -> "Alice, Bob and 4 others can help" (4 resolved,
 *                                            6 announced - the tail reflects the 6)
 *   ([], 1) -> "someone can help"
 *   ([], 2) -> "people can help"
 */
export function buildMatchArrivalTitle(firstNames: string[], newHelperCount: number): string {
  const names = firstNames.map(n => n.trim()).filter(n => n.length > 0);

  if (names.length === 0) {
    return newHelperCount >= 2 ? 'people can help' : 'someone can help';
  }
  if (names.length === 1) return `${names[0]} can help`;
  if (names.length === 2) return `${names[0]} and ${names[1]} can help`;
  if (names.length === 3) return `${names[0]}, ${names[1]} and ${names[2]} can help`;

  // Math.max guards against the announced count under-covering the resolved names: normally
  // newHelperCount >= names.length (names are a subset of the announced arrivals), so this
  // uses newHelperCount; if the two ever diverge the other way, it falls back to names.length
  // so the tail never claims fewer "others" than the two named already imply.
  const others = Math.max(names.length, newHelperCount) - 2;
  return `${names[0]}, ${names[1]} and ${others} others can help`;
}

/**
 * True when `pathname` is the detail screen for this question - the moment the reveal
 * owns, so the banner must stay silent there. Matches both the slug- and id-routed URL
 * forms (`getQuestionPath` prefers the slug, but a viewer may have arrived via the id).
 */
export function isViewingQuestion(
  pathname: string,
  ids: { id?: string | null; slug?: string | null },
): boolean {
  const segments = [ids.slug, ids.id].filter(
    (s): s is string => typeof s === 'string' && s.length > 0,
  );
  return segments.some(seg => pathname === `/questions/${seg}`);
}
